"use client";
import React, { createContext, useState, useContext, useEffect } from "react";
import { BASE_URL } from "../config/apiConfig";

export const TaskBugsContext = createContext();

export const TaskBugsProvider = ({ children }) => {
  const [bugs, setBugs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Get token
  const getToken = () => localStorage.getItem("token");

  // Safe JSON parser
  const parseJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  // -------------------------------------------------------------
  // FETCH ALL BUGS
  // -------------------------------------------------------------
  const fetchAllBugs = async () => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();
      if (!token) return;

      const res = await fetch(`${BASE_URL}/testing-bugs`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await parseJson(res);

     if (res.ok && data?.status === 200) {
  setBugs(data.bugs || []);
} else {
        setError(data?.message || "Failed to fetch bug list");
      }
    } catch (err) {
      console.error(err);
      setError("Error fetching bugs");
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------------------------------------
  // FETCH BUGS BY TASK ID
  // -------------------------------------------------------------
  const fetchBugsByTask = async (taskId) => {
    if (!taskId) return [];

    setLoading(true);
    setError("");

    try {
      const token = getToken();
      if (!token) return [];

      const res = await fetch(`${BASE_URL}/task-bugs/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await parseJson(res);

      if (res.ok && data?.status === 200) {
        return data.bugs || [];
      }

      setError(data?.message || "Failed to fetch bugs by task");
      return [];
    } catch (err) {
      console.error(err);
      setError("Error fetching bugs by task");
      return [];
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------------------------------------
  // CREATE BUG
  // -------------------------------------------------------------
  const createBug = async (formData) => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/testing-bugs`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await parseJson(res);

      if (res.ok && data?.status === 201) {
        await fetchAllBugs();
        return {
          success: true,
          message: data.message,
        };
      }

      return {
        success: false,
        message: data?.message || "Failed to create bug",
      };
    } catch (err) {
      console.error(err);
      return {
        success: false,
        message: "Error creating bug",
      };
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------------------------------------
  // UPDATE BUG
  // -------------------------------------------------------------
  const updateBug = async (id, formData) => {
    setLoading(true);

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/testing-bugs/${id}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await parseJson(res);

      return res.ok && data?.status === 200
        ? {
            success: true,
            message: data.message,
          }
        : {
            success: false,
            message: data?.message || "Failed to update bug",
          };
    } catch (err) {
      console.error(err);
      return {
        success: false,
        message: "Error updating bug",
      };
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------------------------------------
  // DELETE BUG
  // -------------------------------------------------------------
  const deleteBug = async (id, delete_audit_id) => {
    setLoading(true);

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/testing-bugs/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ delete_audit_id }),
      });

      const data = await parseJson(res);

      return res.ok && data?.status === 200
        ? {
            success: true,
            message: data.message,
          }
        : {
            success: false,
            message: data?.message || "Failed to delete bug",
          };
    } catch (err) {
      console.error(err);
      return {
        success: false,
        message: "Error deleting bug",
      };
    } finally {
      setLoading(false);
    }
  };

  // AUTO FETCH
  useEffect(() => {
    const token = getToken();
    if (token) {
      fetchAllBugs();
    }
  }, []);

  return (
    <TaskBugsContext.Provider
      value={{
        bugs,
        loading,
        error,
        fetchAllBugs,
        fetchBugsByTask,
        createBug,
        updateBug,
        deleteBug,
      }}
    >
      {children}
    </TaskBugsContext.Provider>
  );
};

export const useTaskBugs = () => useContext(TaskBugsContext);